import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_CONFIG } from '../../config/apiConfig';
import './index.css';
import {showToast} from "../../utils/messageUtil";

export default function TimeSelect() {
    const navigate = useNavigate();
    const location = useLocation();
    
    // 状态管理
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [vehicleUsageTimes, setVehicleUsageTimes] = useState([]);
    // 可预约时间信息（从车辆选择页面传递过来）
    const [availableTimeInfo, setAvailableTimeInfo] = useState(null);
    // 选择的开始和结束时间
    const [selectedStartTime, setSelectedStartTime] = useState(null);
    const [selectedEndTime, setSelectedEndTime] = useState(null);
    // 预约详情弹窗
    const [showReservationDetail, setShowReservationDetail] = useState(false);
    const [hoveredReservation, setHoveredReservation] = useState(null);
    // 用于跟踪弹窗位置
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

    // 计算默认时间：开始时间为当前时间的整点，结束时间为下一个小时
    const getDefaultTimes = () => {
        const now = new Date();
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        // 开始时间：当前时间（包含小时和分钟）
        const startDateTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), currentHour, currentMinute, 0);

        // 结束时间：开始时间 + 1小时
        const endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000);

        // 格式化为 datetime-local 格式
        const formatToLocal = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        return {
            startTime: formatToLocal(startDateTime),
            endTime: formatToLocal(endDateTime)
        };
    };

    const defaultTimes = getDefaultTimes();

    // 手动选择的开始和结束时间（datetime-local格式：YYYY-MM-DDTHH:mm）
    const [manualStartTime, setManualStartTime] = useState(defaultTimes.startTime);
    const [manualEndTime, setManualEndTime] = useState(defaultTimes.endTime);
    // 记录最后设置时间的方式（'drag' 或 'manual'）
    const [lastSetMethod, setLastSetMethod] = useState(null);
    // 拖拽选择状态
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(null);
    const [dragEndX, setDragEndX] = useState(null);
    // 使用ref追踪时间轴元素，用于拖拽计算
    const timeAxisRef = useRef(null);
    // 出发地、目的地和用车原因
    const [fromLocation, setFromLocation] = useState('');
    const [toLocation, setToLocation] = useState('');
    const [destinations, setDestinations] = useState([]);
    const [reason, setReason] = useState('');
    // 额外目的地输入框状态
    const [showAddDestination, setShowAddDestination] = useState(false);
    const [additionalDestination, setAdditionalDestination] = useState('');
    
    // 从URL参数中获取车辆信息和日期
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const vehicleStr = searchParams.get('vehicle');
        const dateStr = searchParams.get('date');
        const availableTimeStr = searchParams.get('availableTime');
        // 解析可预约时间信息
        if (availableTimeStr) {
            try {
                const availableTimeData = JSON.parse(decodeURIComponent(availableTimeStr));
                setAvailableTimeInfo(availableTimeData);
            } catch (error) {
                console.error('解析可预约时间信息失败:', error);
                setAvailableTimeInfo(null);
            }
        }
        if (vehicleStr) {
            try {
                const vehicle = JSON.parse(vehicleStr);
                setSelectedVehicle(vehicle);
                // 更新当前车辆的预约时间，使用车牌号和日期
                const targetDate = dateStr || selectedDate;
                fetchVehicleAvailableTime(vehicle.plateNumber, targetDate);
            } catch (error) {
                console.error('解析车辆信息失败:', error);
                // 如果解析失败，返回车辆选择页面
                navigate('/vehicle-select');
            }
        } else {
            // 如果没有车辆信息，返回车辆选择页面
            navigate('/vehicle-select');
        }

        if (dateStr) {
            setSelectedDate(dateStr);
        }

        // 检查sessionStorage中是否有存储的时间信息
        // 从地图选择页面返回时，会有存储的时间信息
        const storedTimeInfo = sessionStorage.getItem('selectedTimeInfo');
        const currentDate = dateStr || selectedDate;

        // 计算默认时间的函数
        const calculateDefaultTime = (dateStr, hourOffset = 0) => {
            const now = new Date();
            const currentHour = now.getHours();
            const currentMinute = now.getMinutes();

            // 解析所选日期
            const selectedDateParts = dateStr.split('-');
            const selectedDateObj = new Date(
                parseInt(selectedDateParts[0]),
                parseInt(selectedDateParts[1]) - 1,
                parseInt(selectedDateParts[2])
            );

            // 判断所选日期是否是今天
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const isToday = selectedDateObj.getTime() === today.getTime();

            let startDateTime;
            let startMinute = 0;
            let startHour;

            // 如果是今天，根据当前时间设置默认时间
            if (isToday) {
                // hourOffset为0时计算开始时间，为1时计算结束时间
                if (hourOffset === 0) {
                    // 开始时间：当前时间（不做任何提前）
                    startHour = currentHour;
                    startMinute = currentMinute;
                } else {
                    // 结束时间：开始时间+1小时
                    startHour = currentHour + 1;
                    startMinute = currentMinute;
                }

                startDateTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), startHour, startMinute, 0);
            } else {
                // 未来日期，使用与当前时间对应的小时和分钟
                if (hourOffset === 0) {
                    // 开始时间：当前小时和分钟
                    startHour = currentHour;
                    startMinute = currentMinute;
                } else {
                    // 结束时间：开始时间+1小时
                    startHour = currentHour + 1;
                    startMinute = currentMinute;
                }

                startDateTime = new Date(
                    parseInt(selectedDateParts[0]),
                    parseInt(selectedDateParts[1]) - 1,
                    parseInt(selectedDateParts[2]),
                    startHour,
                    startMinute,
                    0
                );
            }

            // 格式化为 datetime-local 格式
            const year = startDateTime.getFullYear();
            const month = String(startDateTime.getMonth() + 1).padStart(2, '0');
            const day = String(startDateTime.getDate()).padStart(2, '0');
            const hours = String(startDateTime.getHours()).padStart(2, '0');
            const minutes = String(startDateTime.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        if (storedTimeInfo) {
            try {
                const timeData = JSON.parse(storedTimeInfo);
                console.log('从sessionStorage读取时间信息:', timeData);
                
                // 检查存储的时间是否与当前选择的日期匹配
                const storedDate = timeData.manualStartTime ? timeData.manualStartTime.split('T')[0] : null;
                const isDateMatch = storedDate === currentDate;
                
                setSelectedStartTime(timeData.selectedStartTime);
                setSelectedEndTime(timeData.selectedEndTime);
                
                // 如果日期不匹配，重新计算默认时间
                if (!isDateMatch) {
                    setManualStartTime(calculateDefaultTime(currentDate));
                    setManualEndTime(calculateDefaultTime(currentDate, 1));
                    setLastSetMethod(null);
                    console.log('日期不匹配，重新计算默认时间');
                } else {
                    setManualStartTime(timeData.manualStartTime || calculateDefaultTime(currentDate));
                    setManualEndTime(timeData.manualEndTime || calculateDefaultTime(currentDate, 1));
                    setLastSetMethod(timeData.lastSetMethod);
                }
                
                // 恢复存储的reason信息
                if (timeData.reason) {
                    setReason(timeData.reason);
                    console.log('恢复存储的用车原因:', timeData.reason);
                }
                console.log('恢复存储的时间信息');
            } catch (error) {
                console.error('解析时间信息失败:', error);
                // 解析失败，使用默认时间
                setSelectedStartTime(null);
                setSelectedEndTime(null);
                setManualStartTime(calculateDefaultTime(currentDate));
                setManualEndTime(calculateDefaultTime(currentDate, 1));
                setLastSetMethod(null);
                console.log('解析时间信息失败，使用默认时间');
            }
        } else {
            // 首次进入时间选择页面，使用默认时间
            setSelectedStartTime(null);
            setSelectedEndTime(null);
            setManualStartTime(calculateDefaultTime(currentDate));
            setManualEndTime(calculateDefaultTime(currentDate, 1));
            setLastSetMethod(null);
            console.log('首次进入时间选择页面，使用默认时间');
        }
        
        // 清除存储的时间信息
        sessionStorage.removeItem('selectedTimeInfo');
        
    }, [location.search, navigate]);
    
    // 当时间选择变化时，存储到sessionStorage
    useEffect(() => {
        // 只有当用户实际设置了时间时，才存储到sessionStorage
        if (selectedStartTime !== null && selectedEndTime !== null || manualStartTime !== null && manualEndTime !== null) {
            const timeInfo = {
                selectedStartTime,
                selectedEndTime,
                manualStartTime,
                manualEndTime,
                lastSetMethod,
                reason  // 添加reason信息
            };
            sessionStorage.setItem('selectedTimeInfo', JSON.stringify(timeInfo));
            console.log('存储时间信息到sessionStorage:', timeInfo);
        }
    }, [selectedStartTime, selectedEndTime, manualStartTime, manualEndTime, lastSetMethod, reason]);
    
    // 检查sessionStorage中是否有选择的地点信息
    const checkLocationInfo = () => {
        // 首先检查selectedLocation（从地图选择页面返回时设置的）
        const selectedLocation = sessionStorage.getItem('selectedLocation');
        console.log('从sessionStorage读取地点信息:', selectedLocation);
        if (selectedLocation) {
            try {
                const locationData = JSON.parse(selectedLocation);
                console.log('解析后的地点信息:', locationData);
                if (locationData.type === 'from') {
                console.log('设置出发地:', locationData.location.name);
                setFromLocation(locationData.location.name);
            } else if (locationData.type === 'to') {
                console.log('设置目的地:', locationData.location.name);
                updateDestinationFromMap(locationData.location.name);
            }
                // 清除sessionStorage中的selectedLocation，避免重复读取
                sessionStorage.removeItem('selectedLocation');
                console.log('清除sessionStorage中的selectedLocation');
            } catch (error) {
                console.error('解析地点信息失败:', error);
            }
        }
        
        // 直接从sessionStorage中读取出发地和目的地信息
        // 这是为了确保能够同时读取两个地点的信息
        const storedFromLocation = sessionStorage.getItem('fromLocation');
        const storedToLocation = sessionStorage.getItem('toLocation');
        
        if (storedFromLocation) {
            console.log('从sessionStorage直接读取出发地:', storedFromLocation);
            setFromLocation(storedFromLocation);
            // 清除sessionStorage中的fromLocation，避免重复读取
            sessionStorage.removeItem('fromLocation');
            console.log('清除sessionStorage中的fromLocation');
        }
        
        if (storedToLocation) {
            console.log('从sessionStorage直接读取目的地:', storedToLocation);
            updateDestinationFromMap(storedToLocation);
            // 清除sessionStorage中的toLocation，避免重复读取
            sessionStorage.removeItem('toLocation');
            console.log('清除sessionStorage中的toLocation');
        }
        
        // 打印当前的地点信息状态
        console.log('当前地点信息状态:', {
            fromLocation: fromLocation,
            toLocation: toLocation
        });
        
        // 打印sessionStorage中的所有地点信息，以便于调试
        console.log('===== sessionStorage中的地点信息 =====');
        console.log('fromLocation:', sessionStorage.getItem('fromLocation'));
        console.log('toLocation:', sessionStorage.getItem('toLocation'));
    };
    
    // 组件加载时检查地点信息
    useEffect(() => {
        // 组件加载时检查地点信息
        // 这确保了从地图选择页面返回时，地点信息能够正确显示
        checkLocationInfo();
    }, []);
    
    // 监听location变化，当从地图选择页面返回时检查地点信息
    useEffect(() => {
        // 当location变化时（比如从地图选择页面返回），检查地点信息
        checkLocationInfo();
    }, [location]);
    
    // 当车辆信息变化时，存储到sessionStorage
    useEffect(() => {
        if (selectedVehicle) {
            const vehicleData = {
                ...selectedVehicle,
                selectedDate: selectedDate
            };
            sessionStorage.setItem('selectedVehicle', JSON.stringify(vehicleData));
            // console.log('存储车辆信息到sessionStorage:', vehicleData);
        }
    }, [selectedVehicle, selectedDate]);
    
    // 当selectedVehicle或selectedDate变化时，检查地点信息
    useEffect(() => {
        // 当selectedVehicle或selectedDate变化时，检查地点信息
        // 这确保了从地图选择页面返回时，地点信息能够正确显示
        if (selectedVehicle) {
            console.log('selectedVehicle或selectedDate变化，检查地点信息');
            checkLocationInfo();
        }
    }, [selectedVehicle, selectedDate]);
    
    // 格式化时间为24小时制
    function formatTime(time) {
        return time.substring(5, 16);
    }
    
    // 计算时间槽的位置和宽度，统一使用24小时
    const calculateTimeSlotPosition = (startTime, endTime, isModal = false) => {
        // 直接从字符串中提取日期和时间，确保使用正确的日期和小时
        const startParts = startTime.split(' ')[1].split(':');
        const endParts = endTime.split(' ')[1].split(':');
        
        // 提取开始和结束小时，确保是整数
        let startHour = parseInt(startParts[0], 10);
        let endHour = parseInt(endParts[0], 10);
        
        console.log(`预约时间计算:`, {
            startTime,
            endTime,
            startHour,
            endHour,
            isModal
        });
        
        // 统一使用24小时
        const maxHours = 24;
        
        // 确保位置在0-24小时范围内
        const normalizedStart = Math.max(0, Math.min(maxHours, startHour));
        const normalizedEnd = Math.max(0, Math.min(maxHours, endHour));
        
        // 计算开始位置：startHour / 24 * 100%
        const left = (normalizedStart / maxHours) * 100;
        
        // 计算宽度，确保只显示到结束时间点
        const hoursDuration = normalizedEnd - normalizedStart;
        const width = (hoursDuration / maxHours) * 100;
        
        console.log(`时间槽位置计算结果:`, {
            startHour,
            endHour,
            normalizedStart,
            normalizedEnd,
            maxHours,
            left,
            width,
            hoursDuration
        });
        
        return { left: `${left}%`, width: `${width}%` };
    };
    
    // 获取车辆当天已预约时间
    const fetchVehicleAvailableTime = async (plateNumber, date = selectedDate) => {
        try {
            const params = {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                startTime: `${date} 00:00:00`,
                endTime: `${date} 23:59:59`,
                plateNumber: plateNumber  // 直接传入车牌号
            };

            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/getVehicleAvailableTime`, params, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.data && response.data.code === 200) {
                const usageTimes = response.data.data || [];

                console.log('API返回的预约数据:', usageTimes);

                // 直接使用后端返回的数据，后端已经根据车牌号和日期筛选过了
                setVehicleUsageTimes(usageTimes);
            } else {
                setVehicleUsageTimes([]);
            }
        } catch (error) {
            console.error('获取车辆已预约时间失败:', error);
            setVehicleUsageTimes([]);
        }
    };

    // 将像素位置转换为小时（支持分钟精度）
    const pixelToHour = (pixel, containerWidth) => {
        // 24小时对应整个容器宽度
        return (pixel / containerWidth) * 24;
    };

    // 将小时（小数）转换为时间字符串（HH:mm）
    const hourToTimeString = (hourDecimal, date) => {
        console.log('hourToTimeString - input hourDecimal:', hourDecimal);
        const hours = Math.floor(hourDecimal);
        const minutes = Math.round((hourDecimal - hours) * 60);

        console.log('hourToTimeString - calculated hours:', hours, 'minutes:', minutes);

        // 处理分钟为60的情况（进位到下一小时）
        if (minutes === 60) {
            const result = `${String(hours + 1).padStart(2, '0')}:00`;
            console.log('hourToTimeString - result (with carry):', result);
            return result;
        }

        const result = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        console.log('hourToTimeString - result:', result);
        return result;
    };


    // 通用拖拽开始处理函数，支持分钟精度
    const startDrag = (e) => {
        e.preventDefault();

        const timeAxis = timeAxisRef.current;
        if (!timeAxis) return;

        const rect = timeAxis.getBoundingClientRect();

        // 获取按下位置
        let startX;
        if (e.touches && e.touches.length > 0) {
            // 触摸事件
            startX = e.touches[0].clientX - rect.left;
            console.log('Touch start detected, startX:', startX);
        } else {
            // 鼠标事件
            startX = e.clientX - rect.left;
        }

        // 计算点击位置，确保不超过时间轴的宽度
        const clickX = Math.max(0, Math.min(startX, rect.width));

        console.log('Drag start - clickX:', clickX, 'containerWidth:', rect.width);

        // 清除之前选择的时间段
        setSelectedStartTime(null);
        setSelectedEndTime(null);

        setDragStartX(clickX);
        setDragEndX(clickX); // 初始化拖拽结束位置与起始位置相同
        setIsDragging(true);
    };


    // 检查指定时间段是否可预约，支持跨天和分钟精度
    const isTimeSlotAvailable = useCallback((startHourDecimal, endHourDecimal, vehicleTimes) => {
        // 转换为完整时间戳，支持跨天和分钟
        const selectedDateTime = new Date(selectedDate);

        // 处理开始时间
        const startHours = Math.floor(startHourDecimal);
        const startMinutes = Math.round((startHourDecimal - startHours) * 60);
        const startDateTime = new Date(selectedDateTime);
        startDateTime.setHours(startHours, startMinutes, 0, 0);

        // 处理结束时间
        const endHours = Math.floor(endHourDecimal);
        const endMinutes = Math.round((endHourDecimal - endHours) * 60);
        const endDateTime = new Date(selectedDateTime);
        endDateTime.setHours(endHours, endMinutes, 0, 0);

        // 获取当前时间
        const now = new Date();
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const selectedDateOnly = new Date(selectedDateTime.getFullYear(), selectedDateTime.getMonth(), selectedDateTime.getDate());

        console.log('检查时间段是否可预约:', {
            startHourDecimal,
            endHourDecimal,
            startDateTime,
            endDateTime,
            vehicleTimes,
            now,
            currentDate,
            selectedDateOnly
        });

        // 1. 检查是否与已预约时段重叠
        const sameDayVehicleTimes = vehicleTimes.filter(time => {
            const timeStart = new Date(time.startTime);
            const timeEnd = new Date(time.endTime);
            const startDateOnly = new Date(timeStart.getFullYear(), timeStart.getMonth(), timeStart.getDate());
            const endDateOnly = new Date(timeEnd.getFullYear(), timeEnd.getMonth(), timeEnd.getDate());
            return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
        });

        if (!checkTimeConflict(startDateTime, endDateTime, sameDayVehicleTimes)) {
            console.log('时间段与已预约时段冲突');
            return false;
        }

        // 2. 检查是否为当前日期且包含已过时间
        if (selectedDateOnly.getTime() === currentDate.getTime()) {
            const currentHourDecimal = now.getHours() + (now.getMinutes() / 60);
            // 如果所选时间段的开始时间 < 当前时间（严格小于），不可预约
            // 注意：允许开始时间等于当前时间，因为绿色区域已经是经过过滤的可预约区域
            if (startHourDecimal < currentHourDecimal) {
                console.log('时间段包含已过时间:', { startHourDecimal, currentHourDecimal });
                return false;
            }
        }

        console.log('时间段可预约');
        return true;
    }, [selectedDate]);

    // 检查两个完整时间段是否冲突
    const checkTimeConflict = (startDateTime, endDateTime, vehicleTimes) => {
        console.log('检查完整时间段是否冲突:', {
            startDateTime,
            endDateTime,
            vehicleTimes
        });

        // 检查是否与已预约时段重叠
        const hasConflict = vehicleTimes.some(time => {
            // 过滤掉startTime或endTime为空或无效的记录
            if (!time.startTime || !time.endTime) {
                console.log('跳过无效的预约记录:', time);
                return false;
            }

            const timeStart = new Date(time.startTime);
            const timeEnd = new Date(time.endTime);

            // 检查日期是否有效
            if (isNaN(timeStart.getTime()) || isNaN(timeEnd.getTime())) {
                console.log('跳过日期无效的预约记录:', {
                    startTime: time.startTime,
                    endTime: time.endTime,
                    timeStart,
                    timeEnd
                });
                return false;
            }

            console.log('检查与已预约时段是否重叠:', {
                timeStart,
                timeEnd,
                newStart: startDateTime,
                newEnd: endDateTime,
                overlaps: !(endDateTime <= timeStart || startDateTime >= timeEnd)
            });

            // 检查两个时间段是否重叠
            // 重叠的情况：新预约的开始时间在已预约时间范围内，或者新预约的结束时间在已预约时间范围内，或者新预约完全包含已预约时间
            return !(endDateTime <= timeStart || startDateTime >= timeEnd);
        });

        console.log('时间段是否冲突:', hasConflict);
        return !hasConflict;
    };

    // 处理时间轴点击事件，支持分钟精度
    const handleTimeAxisClick = (e, vehicle = selectedVehicle) => {
        // 点击时间轴时，清除当前已选择的时间段
        setSelectedStartTime(null);
        setSelectedEndTime(null);

        // 获取时间轴容器
        const timeAxis = e.currentTarget;
        const rect = timeAxis.getBoundingClientRect();
        const timeAxisWidth = rect.width;

        // 计算点击位置的小时数（包含分钟小数）
        const clickX = e.clientX - rect.left;
        const clickHourDecimal = pixelToHour(clickX, timeAxisWidth);

        // 选择1小时的时间段（支持分钟精度）
        const startHourDecimal = Math.max(0, clickHourDecimal);
        const endHourDecimal = Math.min(24, startHourDecimal + 1);

        // 获取该车辆当天及跨天的预约时间
        const vehicleTimes = vehicleUsageTimes.filter(item => {
            if (item.vehicleId !== vehicle.vehicleId) {
                return false;
            }
            const startDateTime = new Date(item.startTime);
            const endDateTime = new Date(item.endTime);
            const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
            const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
            const dateParts = selectedDate.split('-');
            const selectedDateOnly = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
            return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
        });

        // 检查所选时间段是否可预约
        if (isTimeSlotAvailable(startHourDecimal, endHourDecimal, vehicleTimes)) {
            // 更新选择的时间（存储为小时小数）
            setSelectedStartTime(startHourDecimal);
            setSelectedEndTime(endHourDecimal);

            // 更新手动选择时间输入框 - 使用 hourToTimeString 函数精确转换
            const startTimeStr = `${selectedDate}T${hourToTimeString(startHourDecimal, selectedDate)}`;
            const endTimeStr = `${selectedDate}T${hourToTimeString(endHourDecimal, selectedDate)}`;

            setManualStartTime(startTimeStr);
            setManualEndTime(endTimeStr);

            // 更新最后设置方法为拖拽
            setLastSetMethod('drag');
        } else {
            showToast('所选时间段不可预约，请选择其他时间段', 'error');
        }
    };



    // 通用拖拽结束处理函数，支持分钟精度
    const endDrag = () => {
        if (!isDragging || !dragStartX || !dragEndX || !timeAxisRef.current) {
            console.log('endDrag - invalid state:', { isDragging, dragStartX, dragEndX, hasTimeAxis: !!timeAxisRef.current });
            setIsDragging(false);
            return;
        }

        const timeAxis = timeAxisRef.current;
        const rect = timeAxis.getBoundingClientRect();
        const containerWidth = rect.width;

        // 使用实际的鼠标按下和抬起位置计算准确的小时数（包含分钟小数）
        const startX = Math.min(dragStartX, dragEndX);
        const endX = Math.max(dragStartX, dragEndX);

        const startHourDecimal = Math.max(0, pixelToHour(startX, containerWidth));
        let endHourDecimal = Math.min(24, pixelToHour(endX, containerWidth));

        // 确保时间段至少为1分钟
        const minDuration = 1 / 60; // 1分钟 = 1/60小时
        if (endHourDecimal - startHourDecimal < minDuration) {
            endHourDecimal = startHourDecimal + minDuration;
            if (endHourDecimal > 24) {
                endHourDecimal = 24;
            }
        }

        // 限制结束时间不能超过当天23:59（24:00）
        if (endHourDecimal > 24) {
            endHourDecimal = 24;
            console.log('拖拽结束时间超过24:00，已限制为24:00');
        }

        // 获取该车辆当天及跨天的预约时间
        const vehicleTimes = vehicleUsageTimes.filter(item => {
            if (item.vehicleId !== selectedVehicle.vehicleId) {
                return false;
            }
            const startDateTime = new Date(item.startTime);
            const endDateTime = new Date(item.endTime);
            const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
            const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
            const dateParts = selectedDate.split('-');
            const selectedDateOnly = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
            return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
        });

        // 检查所选时间段是否可预约
        if (isTimeSlotAvailable(startHourDecimal, endHourDecimal, vehicleTimes)) {
            // 更新选择的时间（存储为小时小数）
            setSelectedStartTime(startHourDecimal);
            setSelectedEndTime(endHourDecimal);

            // 更新手动选择时间输入框 - 使用 hourToTimeString 函数精确转换
            const startTimeStr = `${selectedDate}T${hourToTimeString(startHourDecimal, selectedDate)}`;
            const endTimeStr = `${selectedDate}T${hourToTimeString(endHourDecimal, selectedDate)}`;

            console.log('Time selection - startTimeStr:', startTimeStr, 'endTimeStr:', endTimeStr);

            setManualStartTime(startTimeStr);
            setManualEndTime(endTimeStr);

            // 更新最后设置方法为拖拽
            setLastSetMethod('drag');
        } else {
            showToast('所选时间段不可预约，请选择其他时间段', 'error');
        }

        setIsDragging(false);
        setDragStartX(null);
        setDragEndX(null);
    };


    // 通用拖拽移动处理函数
    const moveDrag = (e) => {
        if (!isDragging) return;
        
        // 阻止默认行为，避免页面滚动
        e.preventDefault();
        
        const timeAxis = timeAxisRef.current;
        if (!timeAxis) return;
        
        const rect = timeAxis.getBoundingClientRect();
        
        // 获取移动位置
        let moveX;
        if (e.touches && e.touches.length > 0) {
            // 触摸事件
            moveX = e.touches[0].clientX - rect.left;
            console.log('Touch move detected, moveX:', moveX);
        } else {
            // 鼠标事件
            moveX = e.clientX - rect.left;
        }
        
        // 计算点击位置，确保不超过时间轴的宽度
        const clickX = Math.max(0, Math.min(moveX, rect.width));
        
        setDragEndX(clickX);
    };

    
    // 处理时间轴鼠标按下事件，开始拖拽
    const handleTimeAxisMouseDown = (e) => {
        startDrag(e);
    };
    
    // 处理时间轴鼠标移动事件，更新拖拽结束位置
    const handleTimeAxisMouseMove = (e) => {
        moveDrag(e);
    };
    
    // 处理时间轴鼠标抬起事件，结束拖拽并选择时间段
    const handleTimeAxisMouseUp = () => {
        endDrag();
    };
    
    // 处理时间轴鼠标离开事件，结束拖拽
    const handleTimeAxisMouseLeave = () => {
        setIsDragging(false);
        // 重置拖拽状态
        setDragStartX(null);
        setDragEndX(null);
    };
    
    // 处理时间轴触摸开始事件
    const handleTimeAxisTouchStart = (e) => {
        console.log('handleTimeAxisTouchStart called');
        e.preventDefault();
        startDrag(e);
    };
    
    // 处理时间轴触摸移动事件
    const handleTimeAxisTouchMove = (e) => {
        console.log('handleTimeAxisTouchMove called');
        e.preventDefault();
        moveDrag(e);
    };
    
    // 处理时间轴触摸结束事件
    const handleTimeAxisTouchEnd = (e) => {
        console.log('handleTimeAxisTouchEnd called');
        e.preventDefault();
        endDrag();
    };
    
    // 处理时间轴触摸取消事件
    const handleTimeAxisTouchCancel = (e) => {
        console.log('handleTimeAxisTouchCancel called');
        setIsDragging(false);
        // 重置拖拽状态
        setDragStartX(null);
        setDragEndX(null);
    };
    
    // 处理手动开始时间变化，允许用户自定义时分
    const handleManualStartTimeChange = (e) => {
        const value = e.target.value;
        setManualStartTime(value);
        // 清除时间轴选择，因为手动输入是独立的
        setSelectedStartTime(null);
        setSelectedEndTime(null);
        // 更新最后设置方法为手动
        setLastSetMethod('manual');
        
        // 自动计算结束时间：开始时间+60分钟
        if (value) {
            const startParts = value.split('T');
            if (startParts.length === 2) {
                const startDateParts = startParts[0].split('-');
                const startTimeParts = startParts[1].split(':');
                
                const startDateTime = new Date(
                    parseInt(startDateParts[0]),
                    parseInt(startDateParts[1]) - 1,
                    parseInt(startDateParts[2]),
                    parseInt(startTimeParts[0]),
                    parseInt(startTimeParts[1]),
                    0
                );
                
                // 结束时间 = 开始时间 + 60分钟
                const endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000);
                
                const endYear = endDateTime.getFullYear();
                const endMonth = String(endDateTime.getMonth() + 1).padStart(2, '0');
                const endDay = String(endDateTime.getDate()).padStart(2, '0');
                const endHours = String(endDateTime.getHours()).padStart(2, '0');
                const endMinutes = String(endDateTime.getMinutes()).padStart(2, '0');
                
                const newEndTime = `${endYear}-${endMonth}-${endDay}T${endHours}:${endMinutes}`;
                setManualEndTime(newEndTime);
            }
        }
        
        // 自动校验时间是否可预约
        validateManualTime();
    };

    // 处理手动结束时间变化，允许用户自定义时分
    const handleManualEndTimeChange = (e) => {
        const value = e.target.value;
        
        // 校验时长不能低于60分钟
        if (manualStartTime && value) {
            const startParts = manualStartTime.split('T');
            const endParts = value.split('T');
            
            if (startParts.length === 2 && endParts.length === 2) {
                const startDateParts = startParts[0].split('-');
                const startTimeParts = startParts[1].split(':');
                const endDateParts = endParts[0].split('-');
                const endTimeParts = endParts[1].split(':');
                
                const startDateTime = new Date(
                    parseInt(startDateParts[0]),
                    parseInt(startDateParts[1]) - 1,
                    parseInt(startDateParts[2]),
                    parseInt(startTimeParts[0]),
                    parseInt(startTimeParts[1]),
                    0
                );
                
                const endDateTime = new Date(
                    parseInt(endDateParts[0]),
                    parseInt(endDateParts[1]) - 1,
                    parseInt(endDateParts[2]),
                    parseInt(endTimeParts[0]),
                    parseInt(endTimeParts[1]),
                    0
                );
                
                // 计算时间差（分钟）
                const durationMinutes = (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60);
                
                // 如果时长小于60分钟，不更新并提示
                if (durationMinutes < 60) {
                    showToast('预约时长不能低于60分钟', 'error');
                    return;
                }
            }
        }
        
        setManualEndTime(value);
        // 清除时间轴选择，因为手动输入是独立的
        setSelectedStartTime(null);
        setSelectedEndTime(null);
        // 更新最后设置方法为手动
        setLastSetMethod('manual');
        // 自动校验时间是否可预约
        validateManualTime();
    };

    // 校验手动输入的时间是否可预约
    const validateManualTime = useCallback(() => {
        // 如果开始时间或结束时间为空，不校验
        if (!manualStartTime || !manualEndTime) {
            return;
        }

        // 解析开始和结束时间
        const startParts = manualStartTime.split('T');
        const endParts = manualEndTime.split('T');

        if (startParts.length !== 2 || endParts.length !== 2) {
            return;
        }

        const startDateParts = startParts[0].split('-');
        const startTimeParts = startParts[1].split(':');
        const endDateParts = endParts[0].split('-');
        const endTimeParts = endParts[1].split(':');

        // 构建Date对象
        const startDateTime = new Date(
            parseInt(startDateParts[0]),
            parseInt(startDateParts[1]) - 1,
            parseInt(startDateParts[2]),
            parseInt(startTimeParts[0]),
            parseInt(startTimeParts[1]),
            0
        );

        const endDateTime = new Date(
            parseInt(endDateParts[0]),
            parseInt(endDateParts[1]) - 1,
            parseInt(endDateParts[2]),
            parseInt(endTimeParts[0]),
            parseInt(endTimeParts[1]),
            0
        );

        // 检查开始时间是否小于结束时间
        if (startDateTime >= endDateTime) {
            return;
        }

        // 获取该车辆的预约时间，并过滤掉无效记录
        const vehicleTimes = vehicleUsageTimes.filter(item => {
            // 过滤掉vehicleId不匹配的
            if (item.vehicleId !== selectedVehicle.vehicleId) {
                return false;
            }
            // 过滤掉startTime或endTime为空的记录
            if (!item.startTime || !item.endTime) {
                return false;
            }
            // 检查日期是否有效
            const timeStart = new Date(item.startTime);
            const timeEnd = new Date(item.endTime);
            if (isNaN(timeStart.getTime()) || isNaN(timeEnd.getTime())) {
                return false;
            }
            return true;
        });

        // 直接使用完整的DateTime对象进行冲突检测，而不是转换为小时数
        // 这样可以正确处理跨天的情况
        if (!checkTimeConflict(startDateTime, endDateTime, vehicleTimes)) {
            showToast('所选时间段不可预约，请选择其他时间段', 'error');

        }
    }, [manualStartTime, manualEndTime, selectedVehicle, vehicleUsageTimes, checkTimeConflict]);

    // 跳转到地图选择页面选择出发地
    const handleChooseFromLocation = () => {
        // 存储当前页面路径到sessionStorage
        sessionStorage.setItem('previousPath', '/time-select');
        console.log('存储当前页面路径到sessionStorage:', '/time-select');
        // 导航到地图选择页面，并在返回时检查地点信息
        console.log('跳转到地图选择页面选择出发地');
        navigate('/map-select/from');
    };
    
    // 跳转到地图选择页面选择目的地
    const handleChooseToLocation = () => {
        // 存储当前页面路径到sessionStorage
        sessionStorage.setItem('previousPath', '/time-select');
        console.log('存储当前页面路径到sessionStorage:', '/time-select');
        // 导航到地图选择页面，并在返回时检查地点信息
        console.log('跳转到地图选择页面选择目的地');
        navigate('/map-select/to');
    };
    
    // 处理添加额外目的地
    const handleAddDestination = () => {
        // 检查目的地数量限制
        if (destinations.length >= 5) {
            showToast('目的地最多只能添加5个', 'error');
            return;
        }
        
        if (additionalDestination.trim()) {
            // 使用数组管理多个目的地
            const newDestinations = [...destinations, additionalDestination.trim()];
            setDestinations(newDestinations);
            // 更新显示用的字符串
            setToLocation(newDestinations.join('，'));
            // 清空额外目的地输入框
            setAdditionalDestination('');
            // 隐藏额外输入框
            setShowAddDestination(false);
        }
    };
    
    // 删除单个目的地
    const handleRemoveDestination = (index) => {
        const newDestinations = destinations.filter((_, i) => i !== index);
        setDestinations(newDestinations);
        setToLocation(newDestinations.join('，'));
    };
    
    // 处理目的地输入框手动编辑（保留原有目的地）
    const handleToLocationChange = (e) => {
        const value = e.target.value;
        setToLocation(value);
        // 同步更新数组
        if (value) {
            const newDests = value.split('，').filter(d => d.trim());
            setDestinations(newDests);
        } else {
            setDestinations([]);
        }
    };
    
    // 当从地图选择页面返回时更新目的地
    const updateDestinationFromMap = (locationName) => {
        if (locationName) {
            // 如果已有目的地，添加到列表；否则设置为第一个目的地
            if (destinations.length > 0) {
                const newDestinations = [...destinations, locationName];
                setDestinations(newDestinations);
                setToLocation(newDestinations.join('，'));
            } else {
                setDestinations([locationName]);
                setToLocation(locationName);
            }
        }
    };
    
    // 确认选择的时间，返回预约页面
    const confirmTimeSelection = () => {
        // 确定使用哪个时间（优先使用最后设置的时间）
        let finalStartDateTime, finalEndDateTime;
        
        if (manualStartTime && manualEndTime) {
            // 使用手动选择的时间（datetime-local格式）
            // 手动构建Date对象，避免时区转换问题
            const startParts = manualStartTime.split('T');
            const endParts = manualEndTime.split('T');

            if (startParts.length === 2 && endParts.length === 2) {
                const startDateParts = startParts[0].split('-');
                const startTimeParts = startParts[1].split(':');
                const endDateParts = endParts[0].split('-');
                const endTimeParts = endParts[1].split(':');

                // 手动构建Date对象，确保使用用户选择的时间，而不是经过时区转换的时间
                finalStartDateTime = new Date(
                    parseInt(startDateParts[0]),
                    parseInt(startDateParts[1]) - 1,
                    parseInt(startDateParts[2]),
                    parseInt(startTimeParts[0]),
                    parseInt(startTimeParts[1]),
                    0
                );

                finalEndDateTime = new Date(
                    parseInt(endDateParts[0]),
                    parseInt(endDateParts[1]) - 1,
                    parseInt(endDateParts[2]),
                    parseInt(endTimeParts[0]),
                    parseInt(endTimeParts[1]),
                    0
                );
            } else {
                // 如果格式不正确，使用默认的Date构造函数
                finalStartDateTime = new Date(manualStartTime);
                finalEndDateTime = new Date(manualEndTime);
            }
        } else if (selectedStartTime !== null && selectedEndTime !== null) {
            // 使用拖拽选择的时间
            // 转换为具体的日期时间（支持分钟精度）
            finalStartDateTime = new Date(selectedDate);
            const startHours = Math.floor(selectedStartTime);
            const startMinutes = Math.round((selectedStartTime - startHours) * 60);
            finalStartDateTime.setHours(startHours, startMinutes, 0, 0);
            
            finalEndDateTime = new Date(selectedDate);
            const endHours = Math.floor(selectedEndTime);
            const endMinutes = Math.round((selectedEndTime - endHours) * 60);
            finalEndDateTime.setHours(endHours, endMinutes, 0, 0);
        } else {
            showToast('请选择预约时间段', 'error');
            return;
        }
        
        // 确保开始时间小于结束时间
        if (finalStartDateTime >= finalEndDateTime) {
            showToast('开始时间必须小于结束时间', 'error');
            return;
        }
        
        // 校验时长不能低于60分钟
        const durationMinutes = (finalEndDateTime.getTime() - finalStartDateTime.getTime()) / (1000 * 60);
        if (durationMinutes < 60) {
            showToast('预约时长不能低于60分钟', 'error');
            return;
        }
        
        // 检查出发地、目的地和用车原因是否填写
        if (!fromLocation) {
            showToast('请填写出发地', 'error');
            return;
        }
        
        if (!toLocation) {
            showToast('请填写目的地', 'error');
            return;
        }
        
        if (!reason) {
            showToast('请填写用车原因', 'error');
            return;
        }
        
        console.log('确认选择的时间:', {
            finalStartDateTime,
            finalEndDateTime,
            lastSetMethod
        });

        // 检查时间冲突（支持跨天预约）
        // 获取该车辆在选定日期范围内的预约时间
        const selectedDateStr = finalStartDateTime.toISOString().split('T')[0];
        const dateParts = selectedDateStr.split('-');
        const selectedDateOnly = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));

        const vehicleTimes = vehicleUsageTimes.filter(item => {
            if (item.vehicleId !== selectedVehicle.vehicleId) {
                return false;
            }
            // 过滤掉startTime或endTime为空的记录
            if (!item.startTime || !item.endTime) {
                return false;
            }
            // 检查日期是否有效
            const itemStart = new Date(item.startTime);
            const itemEnd = new Date(item.endTime);
            if (isNaN(itemStart.getTime()) || isNaN(itemEnd.getTime())) {
                return false;
            }
            // 检查选定日期是否在预约时间范围内，支持跨天预约
            const startDateOnly = new Date(itemStart.getFullYear(), itemStart.getMonth(), itemStart.getDate());
            const endDateOnly = new Date(itemEnd.getFullYear(), itemEnd.getMonth(), itemEnd.getDate());
            return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
        });

        // 直接使用完整的DateTime对象进行冲突检测，支持跨天
        if (!checkTimeConflict(finalStartDateTime, finalEndDateTime, vehicleTimes)) {
            showToast('所选时间段不可预约，请选择其他时间段', 'error');
            return;
        }
        
        // 转换为 "YYYY-MM-DD HH:mm:ss" 格式，手动构建以避免时区转换问题
        const formatDateTime = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        };
        
        const formattedStartTime = formatDateTime(finalStartDateTime);
        const formattedEndTime = formatDateTime(finalEndDateTime);
        
        console.log('格式化后的时间:', {
            formattedStartTime,
            formattedEndTime
        });
        
        // 保存选择的车辆、时间、出发地、目的地和用车原因到sessionStorage
        const selectedVehicleData = {
            ...selectedVehicle,
            selectedStartTime: formattedStartTime,
            selectedEndTime: formattedEndTime,
            fromLocation,
            toLocation,
            reason
        };
        
        sessionStorage.setItem('selectedVehicle', JSON.stringify(selectedVehicleData));
        
        // 清除sessionStorage中的时间和地点信息，避免下次进入时显示
        sessionStorage.removeItem('selectedTimeInfo');
        sessionStorage.removeItem('fromLocation');
        sessionStorage.removeItem('toLocation');
        console.log('清除sessionStorage中的时间和地点信息');
        
        // 跳转到预约页面
        navigate('/reserve');
    };
    
    // 取消选择，返回车辆选择页面
    const cancelTimeSelection = () => {
        // 清除sessionStorage中的时间和地点信息
        sessionStorage.removeItem('selectedTimeInfo');
        sessionStorage.removeItem('fromLocation');
        sessionStorage.removeItem('toLocation');
        console.log('清除sessionStorage中的时间和地点信息');
        
        navigate('/vehicle-select');
    };
    
    // 生成时间刻度，使用24小时
    const generateTimeTicks = (isModal = false) => {
        const ticks = [];
        const maxHours = 24; // 统一使用24小时
        // 只生成与时间标签对应的刻度（每2小时一个）
        for (let i = 0; i <= maxHours; i += 2) {
            ticks.push(i);
        }
        return ticks;
    };
    
    // 获取车辆状态
    const getVehicleStatus = (vehicle) => {
        const vehicleTimes = vehicleUsageTimes.filter(item => 
            item.vehicleId === vehicle.vehicleId &&
            item.startTime.split(' ')[0] === selectedDate
        );
        
        console.log(`车辆${vehicle.plateNumber}的状态计算:`, {
            vehicleTimes,
            hasTimes: vehicleTimes.length > 0
        });
        
        if (vehicleTimes.length === 0) {
            return 'available';
        }
        
        // 只要有任何预约时间，就返回reserved状态
        return 'reserved';
    };
    
    // 如果没有选择车辆，显示加载状态
    if (!selectedVehicle) {
        return (
            <div className="time-select-page">
                <div className="loading-indicator">
                    <p>加载中...</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="time-select-page">
            <div className="time-select-content">
                
                {/* 车辆信息卡片 */}
                <div className="vehicle-info-card">
                    <div className="vehicle-info-title">{selectedVehicle.plateNumber}</div>
                    <div className="vehicle-info-detail">
                        <span>{selectedVehicle.modelName || '未知车型'}</span>
                        <span className="vehicle-seats-tag">{selectedVehicle.seats || selectedVehicle.seatCount || selectedVehicle.capacity || '未知'}座</span>
                        <span className="vehicle-purpose-tag">{selectedVehicle.group || '通用'}</span>
                    </div>
                    <div className="vehicle-info-date">选择日期: {selectedDate}</div>
                </div>

                {/* 合并的时间选择卡片 */}
                <div className="time-selection-card">

                    {/* 状态说明 */}
                    <div className="status-legend-section">
                    <div className="status-item">
                        <span className="status-dot available"></span>
                        <span>可预约</span>
                    </div>
                    <div className="status-item">
                        <span className="status-dot reserved"></span>
                        <span>已预约</span>
                    </div>
                    <div className="status-item">
                        <span className="status-dot unavailable"></span>
                        <span>不可预约</span>
                    </div>
                    </div>
                    {/* 可预约时间摘要 */}
                    {availableTimeInfo && (
                        <div className="time-axis-summary">
                            {availableTimeInfo.text}
                        </div>
                    )}
                    {/* 24小时时间轴 - 可交互 */}
                    <div className="time-axis-section">
                    {/* 顶部时间标签 */}
                    <div className="time-label">
                        <span>0</span>
                        <span>2</span>
                        <span>4</span>
                        <span>6</span>
                        <span>8</span>
                        <span>10</span>
                        <span>12</span>
                        <span>14</span>
                        <span>16</span>
                        <span>18</span>
                        <span>20</span>
                        <span>22</span>
                        <span>24</span>
                    </div>
                    
                    {/* 获取当前车辆的预约时间 */}
                    {(() => {
                        const vehicleTimes = vehicleUsageTimes.filter(item => 
                            item.vehicleId === selectedVehicle.vehicleId &&
                            item.startTime.split(' ')[0] === selectedDate
                        );
                        
                        return (
                            <div 
                                className={`time-axis time-axis-modal ${getVehicleStatus(selectedVehicle) === 'unavailable' ? 'unavailable' : ''}`}
                                onClick={(e) => handleTimeAxisClick(e, selectedVehicle)}
                                onMouseDown={handleTimeAxisMouseDown}
                                onMouseMove={handleTimeAxisMouseMove}
                                onMouseUp={handleTimeAxisMouseUp}
                                onMouseLeave={handleTimeAxisMouseLeave}
                                onTouchStart={handleTimeAxisTouchStart}
                                onTouchMove={handleTimeAxisTouchMove}
                                onTouchEnd={handleTimeAxisTouchEnd}
                                onTouchCancel={handleTimeAxisTouchCancel}
                                ref={timeAxisRef}
                                style={{ backgroundColor: 'transparent' }}
                            >
                                {/* 移除小时刻度线渲染，避免显示黑色竖线 */}
                                
                                {/* 渲染已预约时段 */}
                                {(() => {
                                    const dateParts = selectedDate.split('-');
                                    const selectedDateOnly = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
                                    
                                    // 获取当前车辆的预约时间（支持跨天预约）
                                    const vehicleTimes = vehicleUsageTimes.filter(item => {
                                        if (item.vehicleId !== selectedVehicle.vehicleId) {
                                            return false;
                                        }
                                        const startDateTime = new Date(item.startTime);
                                        const endDateTime = new Date(item.endTime);
                                        const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                                        const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
                                        return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
                                    });
                                    
                                    return vehicleTimes.map((timeSlot, index) => {
                                        // 根据当前选定日期计算预约时段在时间轴上的位置
                                        const startDateTime = new Date(timeSlot.startTime);
                                        const endDateTime = new Date(timeSlot.endTime);
                                        const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                                        const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
                                        
                                        let slotLeft = 0;
                                        let slotWidth = 0;
                                        
                                        // 判断当前选定日期是开始日期、中间日期还是结束日期
                                        if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
                                            // 同一天预约：从开始时间到结束时间
                                            const startParts = timeSlot.startTime.split(' ')[1].split(':');
                                            const startHour = parseInt(startParts[0], 10) + parseInt(startParts[1], 10) / 60;
                                            const endParts = timeSlot.endTime.split(' ')[1].split(':');
                                            const endHour = parseInt(endParts[0], 10) + parseInt(endParts[1], 10) / 60;
                                            slotLeft = (startHour / 24) * 100;
                                            slotWidth = ((endHour - startHour) / 24) * 100;
                                        } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
                                            // 开始日期（非结束日期）：从预约开始时间到24:00
                                            const startParts = timeSlot.startTime.split(' ')[1].split(':');
                                            const startHour = parseInt(startParts[0], 10) + parseInt(startParts[1], 10) / 60;
                                            slotLeft = (startHour / 24) * 100;
                                            slotWidth = ((24 - startHour) / 24) * 100;
                                        } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
                                            // 结束日期（非开始日期）：从0:00到预约结束时间
                                            const endParts = timeSlot.endTime.split(' ')[1].split(':');
                                            const endHour = parseInt(endParts[0], 10) + parseInt(endParts[1], 10) / 60;
                                            slotLeft = 0;
                                            slotWidth = (endHour / 24) * 100;
                                        } else if (selectedDateOnly > startDateOnly && selectedDateOnly < endDateOnly) {
                                            // 中间日期：全天
                                            slotLeft = 0;
                                            slotWidth = 100;
                                        }
                                        
                                        return (
                                            <div 
                                                key={index} 
                                                className="time-slot"
                                                style={{
                                                    left: `${slotLeft}%`,
                                                    width: slotWidth >= 100 ? '100%' : `max(${slotWidth}%, 40px)`,
                                                    backgroundColor: '#faad14',
                                                    position: 'absolute',
                                                    top: 0,
                                                    height: '100%',
                                                    zIndex: 10,
                                                    opacity: 0.9,
                                                    cursor: 'pointer'
                                                }}
                                                title={`${formatTime(timeSlot.startTime)} - ${formatTime(timeSlot.endTime)}`}
                                            // PC端：鼠标悬停显示
                                            onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setPopupPosition({
                                                    top: rect.top - 120, // 显示在时间轴上方
                                                    left: rect.left + (rect.width / 2) - 150 // 水平居中
                                                });
                                                setHoveredReservation(timeSlot);
                                                setShowReservationDetail(true);
                                            }}
                                            onMouseLeave={(e) => {
                                                // 检查鼠标是否移动到弹窗上
                                                const modal = document.querySelector('.reservation-detail-modal');
                                                if (modal && !modal.contains(e.relatedTarget)) {
                                                    setHoveredReservation(null);
                                                    setShowReservationDetail(false);
                                                }
                                            }}
                                            // 移动端：触摸显示
                                            onTouchStart={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setPopupPosition({
                                                    top: rect.top - 120, // 显示在时间轴上方
                                                    left: rect.left + (rect.width / 2) - 150 // 水平居中
                                                });
                                                setHoveredReservation(timeSlot);
                                                setShowReservationDetail(true);
                                            }}
                                            onTouchEnd={() => {
                                                setHoveredReservation(null);
                                                setShowReservationDetail(false);
                                            }}
                                            onTouchCancel={() => {
                                                setHoveredReservation(null);
                                                setShowReservationDetail(false);
                                            }}
                                        ></div>
                                    );
                                });
                            })()}
                                
                                {/* 渲染拖拽选择的临时时间段 */}
                                {isDragging && dragStartX !== null && dragEndX !== null && timeAxisRef.current && (
                                    <div 
                                        className="time-slot-dragging"
                                        style={{
                                            position: 'absolute',
                                            height: '100%',
                                            left: `${Math.min(dragStartX, dragEndX) / timeAxisRef.current.offsetWidth * 100}%`,
                                            width: `${Math.abs(dragEndX - dragStartX) / timeAxisRef.current.offsetWidth * 100}%`,
                                            backgroundColor: 'rgba(24, 144, 255, 0.7)',
                                            zIndex: 101,
                                            borderRadius: '4px'
                                        }}
                                    ></div>
                                )}
                                
                                {/* 渲染已选择的时间段 */}
                                {selectedStartTime !== null && selectedEndTime !== null && (
                                    <div
                                        className="time-slot-selected"
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            height: '100%',
                                            left: `${(selectedStartTime / 24) * 100}%`,
                                            width: `${((selectedEndTime - selectedStartTime) / 24) * 100}%`,
                                            backgroundColor: 'rgba(9, 109, 217, 0.85)',
                                            zIndex: 99,
                                            borderRadius: '4px',
                                            border: '2px solid #0050b3',
                                            boxShadow: '0 2px 8px rgba(9, 109, 217, 0.4)'
                                        }}
                                    ></div>
                                )}


                                {/* 渲染不同状态的时段 */}
                                {(() => {
                                    // 获取当前车辆的预约时间（支持跨天预约）
                                    const vehicleTimes = vehicleUsageTimes.filter(item => {
                                        if (item.vehicleId !== selectedVehicle.vehicleId) {
                                            return false;
                                        }
                                        const startDateTime = new Date(item.startTime);
                                        const endDateTime = new Date(item.endTime);
                                        const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                                        const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
                                        const dateParts = selectedDate.split('-');
                                        const selectedDateOnly = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
                                        return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
                                    });

                                    const now = new Date();
                                    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                    const dateParts = selectedDate.split('-');
                                    const selectedDateOnly = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));

                                    let currentTime = -1;

                                    if (selectedDateOnly.getTime() === currentDate.getTime()) {
                                        currentTime = now.getHours() + (now.getMinutes() / 60);
                                    }

                                    const mergedSlots = [];
                                    const sortedVehicleTimes = [...vehicleTimes].sort((a, b) => {
                                        return new Date(a.startTime) - new Date(b.startTime);
                                    });

                                    for (const time of sortedVehicleTimes) {
                                        const startDateTime = new Date(time.startTime);
                                        const endDateTime = new Date(time.endTime);
                                        const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                                        const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

                                        let startHour, endHour;

                                        if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
                                            startHour = startDateTime.getHours() + startDateTime.getMinutes() / 60;
                                            endHour = endDateTime.getHours() + endDateTime.getMinutes() / 60;
                                        } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
                                            startHour = startDateTime.getHours() + startDateTime.getMinutes() / 60;
                                            endHour = 24;
                                        } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
                                            startHour = 0;
                                            endHour = endDateTime.getHours() + endDateTime.getMinutes() / 60;
                                        } else {
                                            startHour = 0;
                                            endHour = 24;
                                        }

                                        if (mergedSlots.length === 0) {
                                            mergedSlots.push({ start: startHour, end: endHour });
                                        } else {
                                            const lastSlot = mergedSlots[mergedSlots.length - 1];
                                            if (startHour <= lastSlot.end) {
                                                lastSlot.end = Math.max(lastSlot.end, endHour);
                                            } else {
                                                mergedSlots.push({ start: startHour, end: endHour });
                                            }
                                        }
                                    }

                                    const finalSlots = [];
                                    let lastEnd = 0;

                                    for (const reservedSlot of mergedSlots) {
                                        if (currentTime >= 0 && lastEnd < currentTime) {
                                            const unavailableEnd = Math.min(reservedSlot.start, currentTime);
                                            if (unavailableEnd > lastEnd) {
                                                finalSlots.push({
                                                    start: lastEnd,
                                                    end: unavailableEnd,
                                                    status: 'unavailable'
                                                });
                                            }
                                            lastEnd = unavailableEnd;
                                        }

                                        if (reservedSlot.start > lastEnd) {
                                            finalSlots.push({
                                                start: lastEnd,
                                                end: reservedSlot.start,
                                                status: 'available'
                                            });
                                        }

                                        const reservedStart = Math.max(reservedSlot.start, currentTime >= 0 ? currentTime : 0);
                                        if (reservedStart < reservedSlot.end) {
                                            finalSlots.push({
                                                start: reservedStart,
                                                end: reservedSlot.end,
                                                status: 'reserved'
                                            });
                                        }

                                        lastEnd = reservedSlot.end;
                                    }

                                    if (lastEnd < 24) {
                                        if (currentTime >= 0 && lastEnd < currentTime) {
                                            finalSlots.push({
                                                start: lastEnd,
                                                end: currentTime,
                                                status: 'unavailable'
                                            });
                                            lastEnd = currentTime;
                                        }

                                        if (lastEnd < 24) {
                                            finalSlots.push({
                                                start: lastEnd,
                                                end: 24,
                                                status: 'available'
                                            });
                                        }
                                    }

                                    if (mergedSlots.length === 0) {
                                        if (currentTime >= 0) {
                                            finalSlots.push({
                                                start: 0,
                                                end: currentTime,
                                                status: 'unavailable'
                                            });
                                            finalSlots.push({
                                                start: currentTime,
                                                end: 24,
                                                status: 'available'
                                            });
                                        } else {
                                            finalSlots.push({
                                                start: 0,
                                                end: 24,
                                                status: 'available'
                                            });
                                        }
                                    }

                                    const slots = finalSlots;
                                    const validSlots = slots.filter(slot => slot.start < slot.end);

                                    return validSlots.map((slot, index) => {
                                        const left = `${(slot.start / 24) * 100}%`;
                                        const width = `${((slot.end - slot.start) / 24) * 100}%`;

                                        let backgroundColor;
                                        switch (slot.status) {
                                            case 'available':
                                                backgroundColor = 'rgba(82, 196, 26, 0.6)';
                                                break;
                                            case 'reserved':
                                                backgroundColor = 'rgba(250, 173, 20, 0.8)';
                                                break;
                                            case 'unavailable':
                                                backgroundColor = 'rgba(191, 191, 191, 0.8)';
                                                break;
                                            default:
                                                backgroundColor = 'rgba(204, 204, 204, 0.3)';
                                        }

                                        return (
                                            <div
                                                key={index}
                                                className={`time-slot-status ${slot.status}`}
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: left,
                                                    width: ((slot.end - slot.start) / 24) * 100 >= 100 ? '100%' : `max(${((slot.end - slot.start) / 24) * 100}%, 2px)`,
                                                    bottom: 0,
                                                    backgroundColor: backgroundColor,
                                                    pointerEvents: 'none',
                                                    borderRadius: '4px'
                                                }}
                                            ></div>
                                        );
                                    });
                                })()}

                            </div>
                        );
                    })()}</div>
                    
                    {/* 已选择时间信息 */}
                    <div className="selected-time-info">
                        {selectedStartTime !== null && selectedEndTime !== null && (
                            <p>已选择: {hourToTimeString(selectedStartTime, selectedDate)} - {hourToTimeString(selectedEndTime, selectedDate)}</p>
                        )}
                    </div>
                    
                    {/* 选择方式提示 */}
                    <div className="time-selection-divider">请拖拽时间轴或手动设置时间</div>
                    
                    {/* 合并的时间和地点选择 */}
                    <div className="combined-selection-section">
                        <div className="time-location-grid">
                            <div className="form-group">
                                <label>开始时间:<span className="required">*</span></label>
                                <div>
                                    <input
                                        type="datetime-local"
                                        className="time-input"
                                        value={manualStartTime}
                                        onChange={handleManualStartTimeChange}
                                        step="60"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>结束时间:<span className="required">*</span></label>
                                <div>
                                    <input
                                        type="datetime-local"
                                        className="time-input"
                                        value={manualEndTime}
                                        onChange={handleManualEndTimeChange}
                                        step="60"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group location-group">
                                <label>出发地:<span className="required">*</span></label>
                                <div className="location-input-container">
                                    <input
                                        type="text"
                                        value={fromLocation}
                                        onChange={(e) => setFromLocation(e.target.value)}
                                        required
                                        className="location-input"
                                        placeholder="请输入出发地"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleChooseFromLocation}
                                        className="location-btn"
                                    >
                                        📍
                                    </button>
                                </div>
                            </div>

                            
                            <div className="form-group location-group">
                                <label>目的地:<span className="required">*</span></label>
                                <div className="location-input-container">
                                    <input
                                        type="text"
                                        value={toLocation}
                                        onChange={handleToLocationChange}
                                        required
                                        className="location-input destination-input"
                                        placeholder="请输入目的地"
                                        title={toLocation}
                                    />
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            console.log('点击目的地选择按钮');
                                            handleChooseToLocation();
                                        }}
                                        className="location-btn"
                                    >
                                        📍
                                    </button>
                                </div>
                                {/* 已添加的目的地标签 */}
                                {destinations.length > 0 && (
                                    <div className="destinations-tags">
                                        {destinations.map((dest, index) => (
                                            <span key={index} className="destination-tag">
                                                {dest}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveDestination(index)}
                                                    className="remove-tag-btn"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {/* 添加目的地按钮 */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (destinations.length >= 5) {
                                            showToast('目的地最多只能添加5个', 'error');
                                            return;
                                        }
                                        setShowAddDestination(!showAddDestination);
                                    }}
                                    className={`add-destination-btn ${destinations.length >= 5 ? 'disabled' : ''}`}
                                    disabled={destinations.length >= 5}
                                >
                                    ➕ 添加目的地 ({destinations.length}/5)
                                </button>
                                {/* 额外目的地输入框 */}
                                {showAddDestination && (
                                    <div className="additional-destination-container">
                                        <input
                                            type="text"
                                            value={additionalDestination}
                                            onChange={(e) => setAdditionalDestination(e.target.value)}
                                            className="location-input additional-destination-input"
                                            placeholder="请输入其他目的地"
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleAddDestination();
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddDestination}
                                            className="confirm-add-btn"
                                        >
                                            确认添加
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* 用车原因 */}
                        <div className="form-group textarea-group">
                            <label>用车原因:<span className="required">*</span></label>
                            <div>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={3}
                                    required
                                    className="reason-textarea"
                                    placeholder="请输入用车原因，接待客户须填写具体客户信息！"
                                ></textarea>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="modal-btn modal-btn-confirm" onClick={confirmTimeSelection}>
                        确定
                    </button>
                    <button className="modal-btn modal-btn-cancel" onClick={cancelTimeSelection}>
                        取消
                    </button>
                </div>
            </div>
            {/* 预约详情弹窗 */}
            {showReservationDetail && hoveredReservation && (
                <div 
                    className="reservation-detail-modal"
                    style={{
                        top: popupPosition.top,
                        left: popupPosition.left
                    }}
                >
                    <div className="reservation-detail-content">
                        <div className="reservation-detail-body">
                            <div className="reservation-detail-item">
                                <span className="label">预约时间：</span>
                                <span>{formatTime(hoveredReservation.startTime)} - {formatTime(hoveredReservation.endTime)}</span>
                            </div>
                            {hoveredReservation.applicantName && (
                                <div className="reservation-detail-item">
                                    <span className="label">申请人：</span>
                                    <span>{hoveredReservation.applicantName}</span>
                                </div>
                            )}
                            {hoveredReservation.applicantDept && (
                                <div className="reservation-detail-item">
                                    <span className="label">所属部门：</span>
                                    <span>{hoveredReservation.applicantDept}</span>
                                </div>
                            )}
                            {hoveredReservation.applicantPhone && (
                                <div className="reservation-detail-item">
                                    <span className="label">联系电话：</span>
                                    <span>{hoveredReservation.applicantPhone}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}